/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect, useMemo } from 'react'
import { isEmpty } from 'remeda'
import {
  type ApolloError,
  gql,
  type TypedDocumentNode,
  useQuery,
} from '@apollo/client'
import type {
  UseProductsProductConnection,
  UseProductsProductEdge,
} from '@velasca/types'
import { handleError } from '@velasca/utils/handle-error'

interface ProductConnectionVariables {
  query: string
}

interface ProductConnectionData {
  products: UseProductsProductConnection
}

const GET_PRODUCTS: TypedDocumentNode<
  ProductConnectionData,
  ProductConnectionVariables
> = gql`
  query getProducts($query: String) {
    products(first: 15, query: $query) {
      edges {
        node {
          isGiftCard
          id
          handle
          title
          productType
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          compareAtPriceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          media(first: 15) {
            edges {
              node {
                ... on MediaImage {
                  image {
                    url
                  }
                }
              }
            }
          }
          variants(first: 30) {
            edges {
              node {
                id
                quantityAvailable
                selectedOptions {
                  name
                  value
                }
              }
            }
          }

          color: metafield(namespace: "custom", key: "color") {
            value
          }
          colorShort: metafield(namespace: "custom", key: "color_short") {
            value
          }
          material: metafield(namespace: "custom", key: "material") {
            value
          }
          materialShort: metafield(namespace: "custom", key: "material_short") {
            value
          }
          reviewsCount: metafield(namespace: "stamped", key: "reviews_count") {
            value
          }
          reviewsAverage: metafield(
            namespace: "stamped"
            key: "reviews_average"
          ) {
            value
          }
          customProductType: metafield(
            namespace: "custom"
            key: "product_type"
          ) {
            value
          }
          staffPickIconID: metafield(
            namespace: "custom"
            key: "staff_pick_icon_id"
          ) {
            value
          }
          isNew: metafield(namespace: "custom", key: "is_new") {
            value
          }
          frontImageIndex: metafield(
            namespace: "custom"
            key: "front_image_index"
          ) {
            value
          }
          hoverImageIndex: metafield(
            namespace: "custom"
            key: "hover_image_index"
          ) {
            value
          }
          collectionBrandID: metafield(
            namespace: "custom"
            key: "collection_brand_id"
          ) {
            value
          }
          collectionBrandName: metafield(
            namespace: "custom"
            key: "collection_brand_name"
          ) {
            value
          }
          seasonID: metafield(namespace: "custom", key: "season_id") {
            value
          }
          seasonName: metafield(namespace: "custom", key: "season_name") {
            value
          }
        }
      }
    }
  }
`

/**
 * Fetches products from the API based on the provided product IDs.
 * We need to use this hook when we need to preserve the order of the products as they were initially passed in.
 *
 * @param productIDs - An array of product IDs to fetch from the API.
 */
export const useSliderProducts = (
  productIDs: string[] = [],
  skip = false,
): {
  loading: boolean
  products: UseProductsProductEdge[]
  error: ApolloError | undefined
} => {
  const { loading, data, error } = useQuery(GET_PRODUCTS, {
    variables: {
      // we need to do this because of the Shopify API (https://shopify.dev/docs/api/admin-graphql/2023-10/queries/products)
      query: productIDs.map((id) => `id:${id}`).join(' OR '),
    },
    fetchPolicy: 'no-cache',
    skip: skip || !productIDs || isEmpty(productIDs),
  })

  useEffect(() => {
    handleError(error, {
      message: "Couldn't fetch products",
      behaviour: 'throw',
    })
  }, [error])

  // We need a particular sort order for these products.
  // The GraphQL query returns them ordered by ID, instead of returning them in the order they initially were. So we need to reorder them with the original order.
  // Useful for "recent products" and product sliders where we need to preserve the order of the items decided by the content team.
  const products = useMemo(() => {
    if (!data?.products?.edges) {
      return []
    }

    return data?.products?.edges?.sort((a, b) => {
      return (
        productIDs.findIndex((productID) => a.node.id.includes(productID)) -
        productIDs.findIndex((productID) => b.node.id.includes(productID))
      )
    })
  }, [data?.products?.edges, productIDs])

  return { loading, products, error }
}
